import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PredefinedDashboard } from "../components/widgetDashboard/widgetDashboard.enums";
import { WidgetDashboardConfig } from "../components/widgetDashboard/widgetDashboard.interface";
import { Broker_OrderType, Product } from "../proto/generated/order";
import { ScalperLayouts } from "../pages/scalper/scalper.typings";
import { QuickTradeProto } from "../proto/generated/quick-trade";
import { SCALPER_DEFAULT_LAYOUT, SCALPER_DEFAULT_PREDEFINED_LAYOUT } from "../assets/constants";
import { Security } from "../proto/generated/candle-data-depot-common";


export type TradeOptions = {
    quickTrade: true,
    orderProduct: Product.INTRADAY,
    orderType: Broker_OrderType,
    quantityMultiplier: number,
    longTradeConfiguration: QuickTradeProto,
    shortTradeConfiguration: QuickTradeProto
}


export interface ScalperStateType {
    predefinedLayout: PredefinedDashboard,
    scalperLayout: ScalperLayouts,
    layout?: WidgetDashboardConfig,
    tradeOptions: TradeOptions,
    initSecurity: Security | null,
    /** Bumps on every layout menu click so same layout can be re-applied after refresh mismatch */
    layoutEpoch: number
}

const initialState: ScalperStateType = {
    predefinedLayout: SCALPER_DEFAULT_PREDEFINED_LAYOUT,
    scalperLayout: SCALPER_DEFAULT_LAYOUT,
    tradeOptions: {
        quickTrade: true,
        orderProduct: Product.INTRADAY,
        orderType: Broker_OrderType.MARKET_ORDER,
        quantityMultiplier: 1,
        longTradeConfiguration: {
            id: "",
            actionName: "Buy Call",
            type: 1,
            referenceId: "",
            templateOrderExecutionParameters: {
                brokerOrderType: 1,
                multiplier: 1,
                strategyLegs: {
                    strategyTemplateLegs: [
                        {
                            id: "leg1",
                            categoryCode: 4,
                            expirySelection: {
                                expirySelectionType: 1
                            },
                            transactionType: 1,
                            lots: 1,
                            optionType: 1,
                            strikeCriteria: {
                                strikeType: 7,
                                referenceType: 1,
                                value: 0,
                                parametersMap: {}
                            }
                        }
                    ]
                }
            },
            description: "Naked long call in strongly bullish market"
        },
        shortTradeConfiguration: {
            id: "",
            actionName: "Buy Put",
            type: 1,
            referenceId: "",
            templateOrderExecutionParameters: {
                brokerOrderType: 1,
                multiplier: 1,
                strategyLegs: {
                    strategyTemplateLegs: [
                        {
                            id: "leg1",
                            categoryCode: 4,
                            expirySelection: {
                                expirySelectionType: 1
                            },
                            transactionType: 1,
                            lots: 1,
                            optionType: 2,
                            strikeCriteria: {
                                strikeType: 7,
                                referenceType: 1,
                                value: 0,
                                parametersMap: {}
                            }
                        }
                    ]
                }
            },
            description: "Naked long put in strongly bearish market"
        }
    },
    initSecurity: null,
    layoutEpoch: 0
};

const normalizeTradeOptions = (tradeOptions?: Partial<TradeOptions>) => {
    if (!tradeOptions) return tradeOptions;

    const { quantityMultiplier } = tradeOptions;
    if (quantityMultiplier === undefined) return tradeOptions;

    return {
        ...tradeOptions,
        quantityMultiplier: Math.max(1, quantityMultiplier)
    };
};

const scalperSlice = createSlice({
    name: "scalper",
    initialState,
    reducers: {
        updateLayout(state, action: PayloadAction<WidgetDashboardConfig>) {
            return {
                ...state,
                layout: action.payload
            }
        },
        setTradeOptions(state, action: PayloadAction<Partial<TradeOptions>>) {
            return {
                ...state,
                tradeOptions: {
                    ...(state?.tradeOptions??{} as any),
                    ...normalizeTradeOptions(action.payload)
                }
            }
        },
        setScalperLayout(state, action: PayloadAction<{predefinedLayout?: PredefinedDashboard, scalperLayout?: ScalperLayouts, force?: boolean}>) {
            const nextScalperLayout = action.payload.scalperLayout ?? state.scalperLayout
            const nextPredefinedLayout = action.payload.predefinedLayout ?? state.predefinedLayout
            const unchanged =
                nextScalperLayout === state.scalperLayout &&
                nextPredefinedLayout === state.predefinedLayout
            return {
                ...state,
                scalperLayout: nextScalperLayout,
                predefinedLayout: nextPredefinedLayout,
                // Always bump on explicit menu clicks (force) so refresh mismatches can re-apply
                layoutEpoch: action.payload.force || !unchanged
                    ? (state.layoutEpoch ?? 0) + 1
                    : state.layoutEpoch
            }
        },
        setScalperState(state, action: PayloadAction<Partial<ScalperStateType>>) {
            return {
                ...state,
                ...action.payload,
                tradeOptions: action.payload.tradeOptions
                    ? {
                        ...(state?.tradeOptions ?? {} as any),
                        ...normalizeTradeOptions(action.payload.tradeOptions)
                    }
                    : state.tradeOptions
            }
        }
    },
});

const { actions, reducer } = scalperSlice;

export const { updateLayout, setScalperLayout, setTradeOptions, setScalperState } = actions;

export default reducer;